/**
 * E002-T016: StreamLain Types
 *
 * Zentrale Type-Definitionen für StreamLain Module.
 */

import type {
  CronPreset,
  TriggerType,
} from '@streamlain/automation-safety-contract';

export type {
  AutomationConfig,
  AutomationOutputMode,
} from './services/automation';
export type {
  AutomationCausation,
  AutomationLoopDiagnostic,
  AutomationTopologyState,
  AutomationTopologyVerdict,
  HttpTarget,
} from '@streamlain/automation-safety-contract';

/**
 * Automation trigger types the editor offers (`row_created` and
 * `row_changed` run the automation for row events of its source table; like
 * `scheduled` they are switched on and off). Single source: the shared
 * automation contract.
 */
export {
  ACTIVATABLE_TRIGGER_TYPES,
  isActivatableTrigger,
  isRowTrigger,
  trackedStatusColumnId,
  type TriggerType,
  watchesStatusColumn,
} from '@streamlain/automation-safety-contract';
// Re-export filter types from data-view
export type {
  ExecutionMode,
  ExecutionProgress,
  ExecutionResult,
  ExecutionState,
  ExecutionStatus,
} from './services/execution';
export type {
  ExecutionLog,
  InputStageLog,
  OutputStageLog,
  ProcessStageLog,
  WorkflowNodeStep,
} from './services/logging';
export type TriggerStatus = 'active' | 'paused' | 'inactive';

/** Cron preset of an automation schedule; the list lives in the contract. */
export type { CronPreset };

export interface TriggerConfig {
  type: TriggerType;
  enabled: boolean;
  cronPreset?: CronPreset;
  timezone?: string;
  debounceMs?: number;
  nextRunAt?: number;
  /**
   * Webhook credential ID this automation listens to.
   * Only meaningful when type === 'webhook'.
   * If null/undefined: no inbound webhook is bound.
   * If set: workspace webhook from Workspace credentials, RBAC-scoped via WorkspaceCredential.
   */
  webhookCredentialId?: string | null;
  /**
   * Columns a `row_changed` trigger watches; empty or missing watches every
   * column of the source table except the status tracking column.
   */
  watchedColumnIds?: string[];
}

export interface TriggerState {
  isActive: boolean;
  type: TriggerType;
  nextRunAt: number | null;
  lastTriggeredAt: number | null;
  status: TriggerStatus;
}

export const DEFAULT_TRIGGER_CONFIG: TriggerConfig = {
  type: 'manual',
  enabled: false,
  cronPreset: 'hourly',
  debounceMs: 5000,
};

/** Virtual inputTableId for webhook payload mappings in InputMapping[]. */
export const WEBHOOK_PAYLOAD_TABLE_ID = '__webhook_payload__';
/** Virtual columnId that represents the full webhook JSON body. */
export const WEBHOOK_PAYLOAD_COLUMN_ID = 'payload';

export type { WorkflowField, WorkflowInfo } from './services/workflow';
export type {
  Filter,
  FilterGroup,
  SingleFilter,
} from '@blocksuite/affine/data-view';
